// Types partagés entre le back-office (Vite/React) et les Edge Functions (Deno).
// Ce dossier `core/` doit rester PUR : aucun import Deno, Node ou navigateur.

export type BrandId = string;

export interface Brand {
  id: BrandId; // 'bjbs', 'badaboum', 'virage'…
  name: string; // Nom affiché (ex. "BONJOUR/BONSOIR")
  domains: string[]; // Domaines mail rattachés (ex. ['badaboum.paris'])
  logo_url: string | null; // URL publique HTTPS (jamais de base64 : limite Gmail 10 000 car.)
  logo_width: number; // largeur d'affichage en px
  logo_height: number | null; // hauteur d'affichage en px (null = proportionnelle, déconseillé pour Outlook)
  primary_color: string; // couleur du nom / titres
  accent_color: string; // couleur secondaire (fonction, séparateurs)
  font_family: string; // pile de polices "web safe"
  website_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  linkedin_url: string | null;
  tiktok_url: string | null;
  address: string | null;
  phone: string | null; // standard du lieu
  legal_mention: string | null; // ex. "L'abus d'alcool est dangereux pour la santé…"
  layout: 'classic' | 'compact';
  is_default: boolean; // lieu utilisé quand aucun domaine ne correspond (un seul à true)
  // Couleurs détaillées (migration 20260917) ; null = repli sur primary_color / accent_color / #1a1a1a.
  name_color: string | null; // couleur du nom
  title_color: string | null; // couleur de la fonction et du lieu
  body_color: string | null; // texte tertiaire : téléphones, e-mail, adresse, site
  social_color: string; // teinte des icônes réseaux ; '#000000' = icônes communes non teintées
  separator_style: 'none' | 'line'; // 'none' = ni bordure ni marge
  separator_color: string | null; // null = accent_color
  separator_width: number; // px
  separator_gap: number; // px de marge de part et d'autre du séparateur
  logo_position: 'left' | 'right' | 'top';
  blocks: BlockConfig[] | null; // ordre et visibilité des blocs ; null ou invalide = ordre par défaut
  /**
   * Arbre de mise en page (docs/EDITEUR-MISE-EN-PAGE.md). Nommé layout_tree car `layout` est déjà
   * le mode classic/compact. null = construit à la volée depuis les champs ci-dessus.
   */
  layout_tree: SignatureLayout | null;
  /**
   * Styles par rôle (docs/REFONTE-CHARTES.md §1) : le style appartient à la charte, jamais à un bloc.
   * null = dérivé des anciennes colonnes de couleur (stylesFromLegacy), rendu strictement identique.
   */
  styles: BrandStyles | null;
}

// ---------------------------------------------------------------- styles par rôle (docs/REFONTE-CHARTES.md §1.1)

/** Rôles de style d'une charte. Correspondance bloc → rôle fixe : voir styles.ts (blockRole). */
export type RoleId = 'name' | 'title' | 'body' | 'legal' | 'social' | 'separator';
export type SocialNetworkId = 'web' | 'instagram' | 'facebook' | 'tiktok' | 'linkedin';
export type TextRoleId = 'name' | 'title' | 'body' | 'legal';
/** Deux graisses seulement : aucune police web « sûre » n'a de 900 (Arial Black est une FAMILLE, pas une graisse). Un « black » venu de la base est lu comme bold. */
export type FontWeight = 'normal' | 'bold';

export interface TextRoleStyle {
  color: string;
  size: number; // px
  weight: FontWeight;
  /** Italique : axe distinct de la graisse (font-style, pas font-weight) ; du gras italique reste possible. Absent = droit. */
  italic?: boolean;
  /** Interlettrage en px uniquement (jamais em : Outlook Windows). 0 = rien d'émis. */
  letterSpacing: number;
  /** Interligne en px, rendu avec mso-line-height-rule:exactly ; null = 1,4 × la taille (défaut du gabarit). */
  lineHeight: number | null;
}

/**
 * Rôle personnalisé, créé par l'utilisateur dans la fiche (« Ajouter un style ») : mêmes propriétés
 * qu'un rôle texte, plus un NOM libre. La clé qui l'identifie (`x-…`, générée à la création) est
 * stable : renommer ne casse aucun bloc qui le référence.
 */
export interface CustomRoleStyle extends TextRoleStyle {
  name: string;
}
/** Référence de rôle portée par un bloc : un rôle texte intégré, ou l'identifiant `x-…` d'un rôle personnalisé. */
export type RoleRef = TextRoleId | string;

export interface BrandStyles {
  name: TextRoleStyle;
  title: TextRoleStyle; // fonction — lieu
  body: TextRoleStyle; // téléphones, e-mail, adresse, site, texte libre
  legal: TextRoleStyle; // mention légale (sixième rôle, décision du 21/09/2026)
  social: { color: string; size: number }; // teinte et taille des icônes réseaux
  separator: { color: string }; // traits de colonne et filets
  /** Rôles nommés par l'utilisateur, par identifiant stable (jsonb : aucune migration). */
  custom?: Record<string, CustomRoleStyle>;
}

// ---------------------------------------------------------------- mise en page (arbre lignes → colonnes → blocs)

/**
 * Champs de la base affichables dans un bloc `field`. Extension : `legal` (mention légale de la
 * charte). Le NOM DU LIEU ne fait plus partie de la signature (décision du 21/09/2026) : le logo
 * porte l'identité, le nom de la charte sert de texte alternatif au logo. Les anciens champs
 * `venue`, `title_venue` et `venue_title` sont convertis (→ `title`) ou ignorés à la lecture.
 */
export type FieldId = 'name' | 'title' | 'phone_mobile' | 'phone_office' | 'email' | 'address' | 'website' | 'legal';

/** [haut, droite, bas, gauche] en px. */
export type Padding = [number, number, number, number];

/** Trait de colonne : épaisseur seulement, la couleur vient du rôle `separator` de la charte. */
export interface LayoutBorder {
  width: number;
}

/**
 * Un bloc ne porte AUCUN style (docs/REFONTE-CHARTES.md §1.3) : seulement sa structure. Couleur,
 * taille, graisse, interlettrage, interligne viennent du rôle de la charte ; l'alignement horizontal
 * est celui de la colonne.
 */
export interface BlockBase {
  id: string;
  padding?: Padding;
  href?: string;
}

/** Ce que le moteur a réellement émis (les blocs vides, colonnes et lignes effondrés sont absents). */
export interface RenderedMap {
  rows: { id: string; cols: { id: string; blocks: string[] }[] }[];
}

export type LayoutBlock = BlockBase & (
  | { type: 'logo' }
  | { type: 'field'; field: FieldId }
  /** Icônes affichées, dans l'ordre du jeu (web d'abord) ; absent = toutes celles dont l'adresse est renseignée. C'est du CONTENU, pas du style. */
  | { type: 'social'; networks?: SocialNetworkId[] }
  | { type: 'banner' }
  /**
   * Texte libre multi-lignes, variables {{prenom}} {{nom}} {{fonction}} {{lieu}} {{email}} {{tel_mobile}}
   * {{tel_fixe}} {{adresse}} {{site}} ; `role` = rôle typographique de la charte (défaut : body), jamais de couleur propre.
   */
  | { type: 'text'; text: string; role?: RoleRef }
  | { type: 'image'; url: string; width: number; height: number }
  | { type: 'spacer'; height: number }
  | { type: 'divider'; width?: number }
);

export interface LayoutCol {
  id: string;
  width: number | string; // px, '50%' ou 'auto'
  valign?: 'top' | 'middle' | 'bottom';
  /** Alignement horizontal du contenu de la colonne (ex-propriété de bloc). */
  align?: 'left' | 'center' | 'right';
  padding?: Padding;
  borderLeft?: LayoutBorder;
  borderRight?: LayoutBorder;
  /** Extensions : le séparateur d'un logo « au-dessus » est une bordure haute de la colonne de texte. */
  borderTop?: LayoutBorder;
  borderBottom?: LayoutBorder;
  backgroundColor?: string;
  blocks: LayoutBlock[];
  /** Sous-lignes (imbrication limitée à 2 niveaux de colonnes). */
  rows?: LayoutRow[];
}

export interface LayoutRow {
  id: string;
  cols: LayoutCol[];
}

export interface SignatureLayout {
  version: 1;
  width: number;
  rows: LayoutRow[];
}

/** Blocs composables d'une signature (voir blocks.ts pour l'ordre par défaut et les libellés). */
export type BlockId =
  | 'name'
  | 'title'
  | 'venue'
  | 'phone_mobile'
  | 'phone_office'
  | 'email'
  | 'address'
  | 'website'
  | 'social'
  | 'legal'
  | 'banner';

export interface BlockConfig {
  id: BlockId;
  visible: boolean;
}

export type PersonKind = 'person' | 'shared';

export interface Person {
  id: string;
  primary_email: string;
  kind: PersonKind; // 'shared' = boîte générique (contact@, booking@…)
  first_name: string | null;
  last_name: string | null;
  display_name: string | null; // prioritaire sur prénom + nom (utile pour les boîtes partagées)
  job_title: string | null;
  phone_mobile: string | null;
  phone_office: string | null;
  brand_id: BrandId | null; // forçage du lieu ; null = déduit du domaine de l'adresse d'envoi
  active: boolean;
  managed: boolean; // false = on ne pousse jamais sa signature (exception)
  no_banner: boolean; // true = aucune bannière (ni campagne ni bannière par défaut), dans les deux modes — boîtes sensibles
  /** Écrit aussi depuis Apple Mail / Outlook : reçoit le mail avec son lien d'installation (jamais les 68). */
  local_client?: boolean;
  send_as_emails: string[]; // adresses d'envoi connues (relevées dans Gmail)
  local_last_seen_at: string | null; // dernière fois qu'une signature locale a affiché sa bannière
  install_token: string; // jeton PRIVÉ de la page d'installation Apple Mail / Outlook
  public_token: string; // jeton PUBLIC des URL de bannière/clic en mode local (visible par les destinataires)
  /** Lien avec l'annuaire Google (posé par la synchro) ; absent sur une ligne saisie à la main. */
  google_id?: string | null;
  google_suspended?: boolean;
}

export interface Campaign {
  id: string;
  name: string;
  image_url: string;
  image_width: number;
  image_height: number;
  link_url: string | null;
  alt: string | null;
  starts_at: string | null; // ISO 8601 ; null = déjà commencée
  ends_at: string | null; // ISO 8601 ; null = sans fin. Sans aucune date : bannière permanente.
  brand_ids: BrandId[]; // vide = tous les lieux
  include_emails: string[]; // vide = tout le monde (dans les lieux ciblés)
  exclude_emails: string[];
  priority: number; // 2 élevée, 1 moyenne, 0 basse (CAMPAIGN_PRIORITY) ; la plus haute gagne
  active: boolean;
  /** UTM automatiques sur le lien de redirection (utm_source=signature, utm_medium=email, utm_campaign=<slug>). */
  utm_auto: boolean;
  updated_at: string;
}

/** Où la signature va être installée. Change la façon dont la bannière est référencée. */
export type RenderMode =
  /** Poussée par l'API Gmail : URL d'image figée et versionnée, re-poussée à chaque changement de campagne. */
  | 'gmail'
  /** Installée à la main dans Apple Mail / Outlook : URL de bannière fixe, résolue côté serveur à chaque affichage. */
  | 'local';

export interface RenderOptions {
  mode: RenderMode;
  /** Base publique des endpoints (ex. https://signatures.bonjourbonsoir.paris). Sans slash final. */
  publicBaseUrl: string;
  /** Date de référence pour choisir la campagne (défaut : maintenant). */
  now?: Date;
}

export interface RenderInput {
  person: Person;
  /** Adresse d'envoi (alias "send as") pour laquelle on génère la signature. */
  sendAsEmail: string;
  brands: Brand[];
  campaigns: Campaign[];
}

export interface RenderResult {
  html: string;
  text: string; // version texte brut (utile pour aperçu / tests)
  brand: Brand;
  campaign: Campaign | null; // campagne retenue (null si bannière par défaut ou aucune)
  length: number; // nombre de caractères du HTML
  warnings: string[];
  /**
   * Carte des nœuds émis, dans l'ordre du DOM : la couche d'interaction de l'éditeur s'en sert pour
   * relier les cellules au tree SANS annoter le HTML (qui reste identique à celui poussé).
   */
  rendered: RenderedMap;
}

export const GMAIL_SIGNATURE_MAX_CHARS = 10_000;

/** Format standard des bannières. Les visuels doivent être exportés en 2x (1200×300) pour les écrans Retina. */
/**
 * Bannières : affichées à 600 px de large, HAUTEUR LIBRE (les dimensions réelles de chaque visuel
 * sont respectées) ; source conseillée 1 200 px de large (facteur 2 pour les écrans Retina),
 * poids ≤ 500 Ko, GIF animé accepté. Plus aucun ratio imposé (décision du 22/09/2026).
 */
export const BANNER_DISPLAY_WIDTH = 600;
export const BANNER_SOURCE_WIDTH = 1200;
export const BANNER_WARN_BYTES = 500_000;
/** Hauteur affichée d'un visuel, proportionnelle à ses dimensions réelles. */
export const bannerDisplayHeight = (w: number, h: number): number => Math.max(1, Math.round((BANNER_DISPLAY_WIDTH * h) / Math.max(1, w)));
